/**
 * vsa-migration.js — VSA Migration Section
 *
 * Owns:
 *   - DOM refs for the VSA migration section
 *   - VSA_MIGRATION_HOURS_PER_VBR constant
 *   - getWindowsVbrCount  — counts Windows VBR servers across all step-1 sites
 *   - syncVsaMigrationSectionVisibility (exported) — show/hide based on Windows VBR presence
 *   - renderVsaMigrationPreview — updates the read-only count + indicative effort display
 *
 * VSA migration means moving Windows VBR servers to Linux (VSA). This is conceptually
 * distinct from hypervisor migration (migrating VM workloads between hypervisors) and
 * lives in its own file for that reason.
 *
 * Exports:
 *   syncVsaMigrationSectionVisibility — called from syncDesignOptionFields + two
 *                                       existingEnvironment change handlers in script.js,
 *                                       and as a safety net at the end of renderSiteList.
 *
 * Window bridge written:
 *   window.vsaMigrationSection — consumed by syncMigrationGroupVisibility in helpers.js
 */

import { sites } from './state.js';
import { getExistingEnvironmentSnapshotQuantity } from './recalc.js';
import { syncMigrationGroupVisibility } from './helpers.js';

// ── DOM refs ──────────────────────────────────────────────────────────────────

const vsaMigrationSection = document.getElementById('design-vsa-migration-section');
window.vsaMigrationSection = vsaMigrationSection;
const vsaVbrCountDisplay   = document.getElementById('vsa-vbr-count-display');
const vsaEffortPreviewDisplay = document.getElementById('vsa-effort-preview');

// ── Constants ─────────────────────────────────────────────────────────────────

// ⚠️  FUTURE WORK — see also recalc.js `vbrJobReimplementationHours`:
// Until this VSA Migration section is fully implemented, the workaround for
// Windows VBR → Linux VSA migrations is: delete the existing Windows VBR in
// Step 2 and add a new Linux VSA lane. recalc.js detects that pattern
// (newVbrLanes.length > 0 + managed jobs) and adds job re-configuration
// effort via `vbrJobReimplementationHours`.
//
// When this section is fully built out and wired into the effort calculation:
//   1. Ensure VSA_MIGRATION_HOURS_PER_VBR (or its replacement formula) covers
//      job re-configuration effort so it is not double-counted with recalc.js.
//   2. Remove or gate `vbrJobReimplementationHours` in recalc.js once this
//      section produces its own effort contribution.
const VSA_MIGRATION_HOURS_PER_VBR = 16;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Count Windows VBR servers across all sites in step 1.
 *  A VBR is counted as Windows when its platform is 'windows' or not yet set
 *  (pre-v13 servers are always Windows; only an explicit 'linux' value means VSA). */
function getWindowsVbrCount() {
    let count = 0;
    for (const site of sites) {
        const total     = getExistingEnvironmentSnapshotQuantity([site], 'vbrServers');
        const platforms = site.existingEnvironmentVbrPlatforms || {};
        for (let i = 0; i < total; i++) {
            if ((platforms[i] || 'windows') !== 'linux') count++;
        }
    }
    return count;
}

/** Update the read-only VBR count and indicative effort preview. */
function renderVsaMigrationPreview(vbrCount) {
    const count = typeof vbrCount === 'number' ? vbrCount : getWindowsVbrCount();
    const hrs = count * VSA_MIGRATION_HOURS_PER_VBR;
    if (vsaVbrCountDisplay) {
        vsaVbrCountDisplay.textContent = count > 0 ? String(count) : '—';
    }
    if (vsaEffortPreviewDisplay) {
        vsaEffortPreviewDisplay.textContent = count > 0 ? `~${hrs} hrs` : '—';
    }
}

// ── Exported API ──────────────────────────────────────────────────────────────

/** Show the VSA migration section only when Windows VBR servers exist in step 1. */
export function syncVsaMigrationSectionVisibility() {
    if (!vsaMigrationSection) return;
    const windowsVbrCount = getWindowsVbrCount();
    const shouldShow = windowsVbrCount > 0;
    if (!shouldShow && vsaMigrationSection.contains(document.activeElement)) { document.activeElement.blur(); }
    vsaMigrationSection.hidden = !shouldShow;
    vsaMigrationSection.style.display = shouldShow ? '' : 'none';
    vsaMigrationSection.setAttribute('aria-hidden', String(!shouldShow));
    if (shouldShow) {
        renderVsaMigrationPreview(windowsVbrCount);
    }
    syncMigrationGroupVisibility();
}
